// realTerms.ts — Inflationsbereinigung (Feature 2, frontend-only): deflates every
// displayed € value in a SimulationResult/MonteCarloResult to the purchasing power of
// the result's own first date, using EACH data point's own date (series points
// individually; waterfall/summary/finalStats "as of" the last date, since those are
// point-in-time aggregates rather than a time series). The ENGINE output stays nominal —
// every function here returns a NEW object, never mutates its input, and nothing
// computed here is sent back to the API or written to localStorage. Percentages
// (probLossVsContributed, probDepleted), dates (including crossover / depletionDate /
// medianDepletionDate), and flags are never deflated — only cents fields are.
import type {
  Bp,
  Cents,
  MonteCarloBandPoint,
  MonteCarloFinalStats,
  MonteCarloResult,
  SeriesPoint,
  SimulationResult,
  Summary,
  Waterfall,
  WithdrawalSummary,
} from '@etf-cockpit/engine';
import { deflate, yearsBetween } from './inflation.js';

export interface RealTermsSettings {
  enabled: boolean;
  inflationPercent: number;
}

export function defaultRealTermsSettings(inflationPercent: number): RealTermsSettings {
  return { enabled: false, inflationPercent };
}

function deflateAt(cents: Cents, refDateIso: string, dateIso: string, inflationBp: Bp): Cents {
  return deflate(cents, yearsBetween(refDateIso, dateIso), inflationBp);
}

export function deflateSeriesPoint(point: SeriesPoint, refDateIso: string, inflationBp: Bp): SeriesPoint {
  const d = (c: Cents) => deflateAt(c, refDateIso, point.date, inflationBp);
  return {
    date: point.date,
    valueCents: d(point.valueCents),
    basisCents: d(point.basisCents),
    settlementCents: d(point.settlementCents),
    contributedCents: d(point.contributedCents),
    cumFeesCents: d(point.cumFeesCents),
    cumGrossGainCents: d(point.cumGrossGainCents),
    cumTerCents: d(point.cumTerCents),
    cumKestCents: d(point.cumKestCents),
  };
}

export function deflateWaterfall(w: Waterfall, refDateIso: string, asOfDateIso: string, inflationBp: Bp): Waterfall {
  const d = (c: Cents) => deflateAt(c, refDateIso, asOfDateIso, inflationBp);
  return {
    contributedCents: d(w.contributedCents),
    feesCents: d(w.feesCents),
    grossGainCents: d(w.grossGainCents),
    terCents: d(w.terCents),
    kestCents: d(w.kestCents),
    netWealthCents: d(w.netWealthCents),
    ...(w.terInformationalCents !== undefined ? { terInformationalCents: d(w.terInformationalCents) } : {}),
  };
}

function deflateWithdrawalSummary(
  ws: WithdrawalSummary,
  refDateIso: string,
  asOfDateIso: string,
  inflationBp: Bp,
): WithdrawalSummary {
  const d = (c: Cents) => deflateAt(c, refDateIso, asOfDateIso, inflationBp);
  return {
    totalGrossWithdrawnCents: d(ws.totalGrossWithdrawnCents),
    totalNetWithdrawnCents: d(ws.totalNetWithdrawnCents),
    totalKestCents: d(ws.totalKestCents),
    depletionDate: ws.depletionDate, // date — never deflated
    remainingValueCents: d(ws.remainingValueCents),
  };
}

export function deflateSummary(s: Summary, refDateIso: string, asOfDateIso: string, inflationBp: Bp): Summary {
  const d = (c: Cents) => deflateAt(c, refDateIso, asOfDateIso, inflationBp);
  return {
    finalValueCents: d(s.finalValueCents),
    finalBasisCents: d(s.finalBasisCents),
    settlementCents: d(s.settlementCents),
    totalKestCents: d(s.totalKestCents),
    unrealisedGainCents: d(s.unrealisedGainCents),
    kestIfSoldNowCents: d(s.kestIfSoldNowCents),
    ...(s.withdrawal ? { withdrawal: deflateWithdrawalSummary(s.withdrawal, refDateIso, asOfDateIso, inflationBp) } : {}),
  };
}

/**
 * Deflates every € value in a SimulationResult: series points individually (their own
 * date), waterfall/summary as of the LAST series date (the "as of now" valuation those
 * aggregates represent). `crossover` (dates only) and `flags` pass through unchanged.
 * `events` (the raw ledger) is intentionally NOT deflated — EventsLedger stays a nominal
 * historical record; the feature spec lists summary/chart/waterfall/compare/Monte-Carlo,
 * not the per-event ledger. inflationBp === 0 or an empty series returns the SAME object
 * (no-op fast path, still never mutates).
 */
export function deflateResult(result: SimulationResult, inflationBp: Bp): SimulationResult {
  if (inflationBp === 0 || result.series.length === 0) return result;
  const refDateIso = result.series[0]!.date;
  const asOfDateIso = result.series[result.series.length - 1]!.date;
  return {
    ...result,
    series: result.series.map((p) => deflateSeriesPoint(p, refDateIso, inflationBp)),
    waterfall: deflateWaterfall(result.waterfall, refDateIso, asOfDateIso, inflationBp),
    summary: deflateSummary(result.summary, refDateIso, asOfDateIso, inflationBp),
  };
}

export function deflateMonteCarloBandPoint(
  p: MonteCarloBandPoint,
  refDateIso: string,
  inflationBp: Bp,
): MonteCarloBandPoint {
  const d = (c: Cents) => deflateAt(c, refDateIso, p.date, inflationBp);
  return { date: p.date, p10: d(p.p10), p25: d(p.p25), p50: d(p.p50), p75: d(p.p75), p90: d(p.p90) };
}

function deflateFinalStats(
  stats: MonteCarloFinalStats,
  refDateIso: string,
  asOfDateIso: string,
  inflationBp: Bp,
): MonteCarloFinalStats {
  const d = (c: Cents) => deflateAt(c, refDateIso, asOfDateIso, inflationBp);
  return {
    p10: d(stats.p10),
    p50: d(stats.p50),
    p90: d(stats.p90),
    mean: d(stats.mean),
    // Probabilities and dates are never deflated.
    probLossVsContributed: stats.probLossVsContributed,
    ...(stats.probDepleted !== undefined ? { probDepleted: stats.probDepleted } : {}),
    ...(stats.medianDepletionDate !== undefined ? { medianDepletionDate: stats.medianDepletionDate } : {}),
  };
}

/** Same convention as deflateResult, applied to a Monte-Carlo band + finalStats. */
export function deflateMonteCarloResult(result: MonteCarloResult, inflationBp: Bp): MonteCarloResult {
  if (inflationBp === 0 || result.band.length === 0) return result;
  const refDateIso = result.band[0]!.date;
  const asOfDateIso = result.band[result.band.length - 1]!.date;
  return {
    ...result,
    band: result.band.map((p) => deflateMonteCarloBandPoint(p, refDateIso, inflationBp)),
    finalStats: deflateFinalStats(result.finalStats, refDateIso, asOfDateIso, inflationBp),
  };
}
